'use client';

import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { FormFieldContext } from '../form-field/context/form-field.context';
import { rangeRecipe } from './range.styles';
import type { RangePropsInterface } from './range.types';

/**
 * A native ["range" input](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/range)
 * element, also commonly known as a slider.
 *
 * @related ['Slider']
 * @since 6.11.0
 */

export const Range = forwardRef<HTMLInputElement, RangePropsInterface>(
  (props: RangePropsInterface, forwardedRef) => {
    const formFieldContext = useContext(FormFieldContext);

    const {
      className,
      css: cssProp,
      step = 1,
      disabled = formFieldContext.disabled || false,
      error = formFieldContext.error || false,
      ...rest
    } = props;

    const styles = css(rangeRecipe.raw({ disabled }), cssProp);

    return (
      <input
        ref={forwardedRef}
        type="range"
        data-c11n-component="Range"
        data-c11n-version={__C11N_VERSION__}
        aria-describedby={formFieldContext.descriptionId}
        aria-invalid={error || undefined}
        id={formFieldContext.controlId}
        step={step}
        disabled={disabled}
        className={cx('c11n', styles, className)}
        {...rest}
      />
    );
  },
);

Range.displayName = 'Range';
